import {z} from "zod/v4-mini"
import {graphNode} from "@cm/graph-v3"
import {
    registerTemplateGraphNode,
    templateLike,
    TemplateGraphInput,
    externalId,
    numberLikeNode,
    NumberExport,
    NumberOutput,
    MaterialExport,
    MaterialOutput,
    NumberInfo,
    MaterialInfo,
    materialLike,
    imageLike,
    stringLikeNode,
    transformLike,
    getTemplateNodeReturns,
    TemplateInstance,
    TemplateGraph,
    TemplateNodes,
    Parameters,
    getInterfaceIdPrefix,
    wrapInterfaceId,
    parseOutputTypeAndOutputIdFromOutputKey,
    outputKeyFromOutputTypeAndOutputId,
    wrapComponentOutputsIds,
    skipped,
    EMPTY_OBJECT,
    EMPTY_LIST,
    ResolveAlias,
    OutputNode,
    TemplateNode,
    isBuilderOutlet,
    NotReady,
    TypeDescriptors,
    visitNone,
} from "@cm/template-nodes"
import type {
    TemplateLikeNode,
    NumberLikeNode,
    MaterialLikeNode,
    ImageLikeNode,
    StringLikeNode,
    TransformLike,
    ExternalId,
    TemplateNodeDefinition,
    OnCompileContext,
    OnFilterActiveContext,
    TemplateNodeContext,
    VisitorNodeResult,
    InterfaceDescriptor,
    ExportNode,
    IModifyTemplateGraph,
    EvaluatedTemplateInputs,
    EvaluatedTemplateOutputs,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
    ParametersParameters,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {mattressComponentGraphLike, mattressBaseMesh, mattressCurveLike, mattressReferenceFrameLike, MATTRESS_BASE_MESH} from "#mattress/validation"
import type {MattressComponentGraphLikeNode, MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {MattressBaseMesh} from "#mattress/validation"
import {MattressCurveOutput} from "#mattress/nodes/curve/mattress-curve-output"
import {MattressReferenceFrameOutput} from "#mattress/nodes/reference-frame/mattress-reference-frame-output"
import {MattressCurveExport} from "#mattress/nodes/curve/mattress-curve-export"
import {MattressReferenceFrameExport} from "#mattress/nodes/reference-frame/mattress-reference-frame-export"
import type {MattressExport} from "#mattress/nodes/mattress-export"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {filterAndWrapInterface, getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"
import {CompileRunMattressComponent} from "#mattress/runtime-graph/nodes/compile-run-mattress-component"
import type {
    MattressComponentInstanceLocalNodesData,
    MattressBaseMeshData,
    EvaluatedMattressComponentInput,
    EvaluatedMattressComponentInputs,
    EvaluatedMattressComponentValueOrigin,
    EvaluatedMattressComponentValueType,
    MattressMeshData,
    MattressNodePath,
    MattressOnCompileContext,
} from "#mattress/nodes/component/mattress-component-types"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import {isMattressCurveLikeNode, isMattressReferenceFrameLikeNode, isMattressComponentGraphLikeNode} from "#mattress/role-markers"
import {EvaluatedMattressComponentInputsTypeDescriptor, EvaluatedTemplateInputsTypeDescriptor} from "#mattress/nodes/component/mattress-component-types"
import {
    MattressTemplateInterfaceDescriptorTypeDescriptor,
    MattressComponentInterfaceDescriptorTypeDescriptor,
    MattressCurveInfo,
    MattressReferenceFrameInfo,
} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {uuid4, hashObject} from "@cm/utils"

import type {Evaluator} from "@cm/graph-v3"

import {Matrix4} from "@cm/math"

const TD = TypeDescriptors

// Lives here (not in mattress-component-helpers) so the helpers module stays a leaf — it would otherwise
// have to value-import all four output/export classes + the Info classes, which import helpers back.
export function outputAndExportInfoForOutputInterfaceDescriptor(
    outputDescriptor: InterfaceDescriptor<unknown> | MattressComponentInterfaceDescriptor<unknown>,
) {
    if (outputDescriptor instanceof MattressCurveInfo)
        return {
            source: "component" as const,
            type: "mattressCurve" as const,
            outputNodeClass: MattressCurveOutput,
            exportNodeClass: MattressCurveExport,
            name: outputDescriptor.props.name,
        }
    else if (outputDescriptor instanceof MattressReferenceFrameInfo)
        return {
            source: "component" as const,
            type: "mattressReferenceFrame" as const,
            outputNodeClass: MattressReferenceFrameOutput,
            exportNodeClass: MattressReferenceFrameExport,
            name: outputDescriptor.props.name,
        }
    else if (outputDescriptor instanceof NumberInfo)
        return {
            source: "template" as const,
            type: "number" as const,
            outputNodeClass: NumberOutput,
            exportNodeClass: NumberExport,
            name: outputDescriptor.props.name,
        }
    else if (outputDescriptor instanceof MaterialInfo)
        return {
            source: "template" as const,
            type: "material" as const,
            outputNodeClass: MaterialOutput,
            exportNodeClass: MaterialExport,
            name: outputDescriptor.props.name,
        }
    throw new Error(`Unsupported output interface descriptor: ${outputDescriptor.constructor.name}`)
}

const forwardedDescriptorListDesc = TD.Array(MattressTemplateInterfaceDescriptorTypeDescriptor)
const mattressTemplateInterfaceDescriptorArrayDesc = forwardedDescriptorListDesc
const mattressComponentInterfaceDescriptorArrayDesc = TD.Array(MattressComponentInterfaceDescriptorTypeDescriptor)
const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

const isMattressBaseMesh = (value: unknown): value is typeof MATTRESS_BASE_MESH => value === MATTRESS_BASE_MESH

export type MattressComponentInstanceSpecificParameterNodeValue = MattressCurveLikeNode | MattressReferenceFrameLikeNode | MattressBaseMesh

export type MattressComponentInstanceParameterNodeValue =
    | MattressComponentInstanceSpecificParameterNodeValue
    | NumberLikeNode
    | MaterialLikeNode
    | TemplateLikeNode
    | ImageLikeNode
    | StringLikeNode
    | TransformLike

export type MattressComponentInstanceParametersParameterValue = MattressComponentInstanceParameterNodeValue | string | number | null

export type MattressComponentInstanceParametersParameters = {
    [key: string]: MattressComponentInstanceParametersParameterValue
}

export const mattressComponentInstanceParametersParameterValue = z.nullable(
    z.union([
        mattressCurveLike,
        mattressReferenceFrameLike,
        mattressBaseMesh,
        numberLikeNode,
        materialLike,
        templateLike,
        imageLike,
        stringLikeNode,
        transformLike,
        z.string(),
        z.number(),
    ]),
)

export type MattressComponentInstanceParameters = {
    name: string
    id: ExternalId
    graph: MattressComponentGraphLikeNode | TemplateLikeNode | null
    parameters: MattressComponentInstanceParametersParameters
    internal?: {
        templateInstance: TemplateInstance
    }
}

const definition: TemplateNodeDefinition<MattressComponentInstanceParameters> = {
    nodeClass: "MattressComponentInstance",
    validation: {
        parameters: z.object({
            name: z.string(),
            id: externalId,
            graph: z.nullable(z.union([mattressComponentGraphLike, templateLike])),
            parameters: z.record(z.string(), mattressComponentInstanceParametersParameterValue),
            internal: z.optional(
                z.object({
                    templateInstance: graphNode(TemplateInstance),
                }),
            ),
        }),
        returns: getTemplateNodeReturns(),
    },
}

function buildInputSourceMappingFromInstanceParameters(parameters: MattressComponentInstanceParametersParameters): Map<string, TemplateNode[]> {
    const inputSourceMapping = new Map<string, TemplateNode[]>()
    for (const [inputId, value] of Object.entries(parameters)) {
        if (value === undefined || value === null) continue
        const sourceNodes: TemplateNode[] = []
        if (value instanceof TemplateNode) sourceNodes.push(value)
        else if (Array.isArray(value))
            for (const item of value as unknown[]) {
                if (item instanceof TemplateNode) sourceNodes.push(item)
            }
        if (sourceNodes.length > 0) inputSourceMapping.set(inputId, sourceNodes)
    }
    return inputSourceMapping
}

function filterMattressComponentInstanceParameters(parameters: MattressComponentInstanceParametersParameters) {
    const regularTemplateInstanceParameters: ParametersParameters = {}
    const mattressComponentInstanceSpecificParameters: {[key: string]: MattressComponentInstanceSpecificParameterNodeValue} = {}

    const isMattressSpecificParameter = (
        _key: string,
        value: MattressComponentInstanceParametersParameterValue,
    ): value is MattressComponentInstanceSpecificParameterNodeValue => {
        if (isMattressBaseMesh(value)) return true
        if (!(value instanceof TemplateNode)) return false
        if (isMattressCurveLikeNode(value)) return true
        if (isMattressReferenceFrameLikeNode(value)) return true
        return false
    }

    for (const [key, value] of Object.entries(parameters)) {
        if (isMattressSpecificParameter(key, value)) {
            mattressComponentInstanceSpecificParameters[key] = value
        } else {
            regularTemplateInstanceParameters[key] = value
        }
    }

    return {regularTemplateInstanceParameters, mattressComponentInstanceSpecificParameters}
}

function evaluateMattressComponentInputsForParameters(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    parameters: {[key: string]: MattressComponentInstanceSpecificParameterNodeValue},
    mattressMeshData: BuilderOutlet<MattressMeshData | null>,
): [BuilderInlet<EvaluatedMattressComponentInputs>, string[]] {
    function setComponentInput(
        scope: GraphBuilderScope,
        inputs: {[inputId: string]: BuilderInlet<EvaluatedMattressComponentInput> | undefined | typeof NotReady},
        type: EvaluatedMattressComponentValueType,
        id: string,
        value: BuilderInlet<EvaluatedMattressComponentInput["value"]>,
        origin?: EvaluatedMattressComponentValueOrigin,
    ) {
        if (isBuilderOutlet(value) || value instanceof ResolveAlias) {
            inputs[id] = scope.struct<EvaluatedMattressComponentInput>("EvaluatedMattressComponentInput", {
                type,
                value,
                origin,
            })
        } else throw new Error(`Unsupported mattress component input value: ${value}`)
    }

    const evaluatedComponentInputs: {[inputId: string]: BuilderInlet<EvaluatedMattressComponentInput> | undefined | typeof NotReady} = {}
    const claimedInputIds: string[] = []

    for (const [inputId, value] of Object.entries(parameters)) {
        if (value === undefined || value === null) continue

        claimedInputIds.push(inputId)

        if (isMattressBaseMesh(value)) {
            const baseMeshData = scope.pureLambda(
                mattressMeshData,
                (mattressMeshData) => (mattressMeshData?.baseMeshLocalNodes?.mesh ? {localNode: mattressMeshData?.baseMeshLocalNodes?.mesh} : null),
                `mattressBaseMeshData-${inputId}`,
            )
            setComponentInput(scope, evaluatedComponentInputs, "mattressBaseMesh", inputId, baseMeshData, value)
        } else if (isMattressCurveLikeNode(value)) {
            setComponentInput(scope, evaluatedComponentInputs, "mattressCurve", inputId, evaluator.evaluate(value), value)
        } else if (isMattressReferenceFrameLikeNode(value)) {
            setComponentInput(scope, evaluatedComponentInputs, "mattressReferenceFrame", inputId, evaluator.evaluate(value), value)
        } else throw new Error(`Unsupported mattress component input parameter value for parameter ${inputId}: ${value}`)
    }

    return [scope.group("evaluatedComponentInputs", evaluatedComponentInputs), claimedInputIds]
}

function getAmbientComponentInputsForInstance(
    scope: GraphBuilderScope,
    instanceId: string,
    ambientInputsData: EvaluatedMattressComponentInputs,
): BuilderOutlet<EvaluatedMattressComponentInputs> {
    const ambientInputsDataForInstance: EvaluatedMattressComponentInputs = {}

    for (const [inputId, value] of Object.entries(ambientInputsData)) {
        const [prefix, unwrappedId] = getInterfaceIdPrefix(inputId)
        if (prefix !== instanceId) continue
        ambientInputsDataForInstance[unwrappedId] = value
    }

    return scope.group("ambientComponentInputsForInstance", ambientInputsDataForInstance)
}

function evaluateComponentInstanceInputsData(
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    instanceId: string,
    instanceParameters: MattressComponentInstanceParametersParameters,
    ambientComponentInputs: EvaluatedMattressComponentInputs,
    mattressMeshData: BuilderOutlet<MattressMeshData | null>,
) {
    const {regularTemplateInstanceParameters, mattressComponentInstanceSpecificParameters} = filterMattressComponentInstanceParameters(instanceParameters)
    const [templateInputsData, claimedTemplateInputsIds] = evaluator.evaluateTemplateInputsForParameters(scope, regularTemplateInstanceParameters, instanceId)

    const [thisComponentInputsData, claimedComponentInputsIds] = evaluateMattressComponentInputsForParameters(
        scope,
        evaluator,
        mattressComponentInstanceSpecificParameters,
        mattressMeshData,
    )
    const ambientComponentInputsData = getAmbientComponentInputsForInstance(scope, instanceId, ambientComponentInputs)

    const componentInputsData = scope.pureLambda(
        scope.tuple(thisComponentInputsData, ambientComponentInputsData),
        ([thisComponentInputsData, ambientComponentInputsData]) => ({
            ...thisComponentInputsData,
            ...ambientComponentInputsData,
        }),
        "componentInputsData",
    )

    return {
        templateInputsData: scope.value(templateInputsData, EvaluatedTemplateInputsTypeDescriptor, "templateInputsData"),
        componentInputsData: scope.value(componentInputsData, EvaluatedMattressComponentInputsTypeDescriptor, "componentInputsData"),
        claimedTemplateInputsIds,
        claimedComponentInputsIds,
    }
}

const LOCAL_EXPORT_FOR_LOCAL_SUB_INSTANCE_OUTPUT_NAME_PREFIX = "localExportForLocalSubInstanceOutput"

function getForwardedLocalInstanceTemplateOutputsForInstance(
    scope: GraphBuilderScope,
    instanceId: string,
    ambientForwardedLocalInstanceTemplateOutputs: EvaluatedTemplateOutputs,
): BuilderOutlet<EvaluatedTemplateOutputs> {
    const ambientForwardedLocalInstanceTemplateOutputsForInstance: EvaluatedTemplateOutputs = {}

    for (const [outputKey, value] of Object.entries(ambientForwardedLocalInstanceTemplateOutputs)) {
        const {outputType, outputId} = parseOutputTypeAndOutputIdFromOutputKey(outputKey)
        const [prefix, unwrappedId] = getInterfaceIdPrefix(outputId)
        if (prefix !== instanceId) continue
        const unwrappedOutputKey = outputKeyFromOutputTypeAndOutputId(outputType, unwrappedId)
        ambientForwardedLocalInstanceTemplateOutputsForInstance[unwrappedOutputKey] = value
    }
    return scope.group("ambientForwardedLocalInstanceTemplateOutputsForInstance", ambientForwardedLocalInstanceTemplateOutputsForInstance)
}

function getForwardedLocalInstanceDescriptorListForInstance(
    scope: GraphBuilderScope,
    instanceId: string,
    ambientForwardedLocalInstanceDescriptorList: InterfaceDescriptor<unknown>[],
): BuilderOutlet<InterfaceDescriptor<unknown>[]> {
    return scope.pureLambda(
        scope.tuple(
            scope.value(scope.list(ambientForwardedLocalInstanceDescriptorList), forwardedDescriptorListDesc, "ambientForwardedLocalInstanceDescriptorList"),
            instanceId,
        ),
        ([ambientForwardedLocalInstanceDescriptorList, instanceId]) => {
            const ambientForwardedLocalInstanceDescriptorListForInstance: InterfaceDescriptor<unknown>[] = []

            for (const descriptor of ambientForwardedLocalInstanceDescriptorList) {
                if (descriptor.props.type === "output" && descriptor.props.name === LOCAL_EXPORT_FOR_LOCAL_SUB_INSTANCE_OUTPUT_NAME_PREFIX) continue
                const [prefix, unwrappedId] = getInterfaceIdPrefix(descriptor.props.id)
                if (prefix !== instanceId) continue
                ambientForwardedLocalInstanceDescriptorListForInstance.push(descriptor.clone(unwrappedId))
            }

            return ambientForwardedLocalInstanceDescriptorListForInstance
        },
        "ambientForwardedLocalInstanceDescriptorListForInstance",
        {
            output: forwardedDescriptorListDesc,
        },
    )
}

function createDummyTemplateInstance(): TemplateInstance {
    return new TemplateInstance({
        parameters: {
            name: "dummy template instance",
            id: uuid4(),
            template: new TemplateGraph({parameters: {name: "dummy template graph", nodes: new TemplateNodes({parameters: {list: []}})}}),
            parameters: new Parameters({parameters: {}}),
            visible: true,
        },
    })
}

//////////////////////////////////////////////////////////////////////////////////////////////

function onCompileImplForMattressComponentGraph(
    instance: MattressComponentInstance,
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    mattressContext: MattressOnCompileContext["mattressContext"],
    parameters: MattressComponentInstanceParameters,
    graph: MattressComponentGraphLikeNode,
) {
    const {templateContext, templateScope} = evaluator
    const {
        evaluatedData,
        nodePath,
        objectPath,
        componentInputs: ambientComponentInputs,
        componentDepth,
        evaluatePartialMattressMeshData,
        exposeClaimedSubComponentInputs,
        forwardedLocalInstanceTemplateOutputs,
        forwardedLocalInstanceDescriptorList,
    } = mattressContext
    const {
        localNodesDataLists,
        curvesDataLists,
        referenceFramesDataLists,
        cutsDataLists,
        componentsDescriptorLists,
        templatesDescriptorLists,
        inputSourceMappings,
        subComponentsOutputs,
        subTemplatesOutputs,
        infoDataLists,
    } = evaluatedData

    const {id, parameters: instanceParameters, internal: internalParameters} = parameters

    const mattressMeshData = evaluatePartialMattressMeshData(scope, evaluator)

    const [graphDataValid] = scope.branch(evaluator.evaluate(graph))

    const {templateInputsData, claimedTemplateInputsIds, componentInputsData, claimedComponentInputsIds} = evaluateComponentInstanceInputsData(
        scope,
        evaluator,
        id,
        instanceParameters,
        ambientComponentInputs,
        mattressMeshData,
    )

    const inputSourceMapping = buildInputSourceMappingFromInstanceParameters(instanceParameters)

    const thisForwardedLocalInstanceTemplateOutputsData = getForwardedLocalInstanceTemplateOutputsForInstance(scope, id, forwardedLocalInstanceTemplateOutputs)
    const thisForwardedLocalInstanceDescriptorListData = getForwardedLocalInstanceDescriptorListForInstance(scope, id, forwardedLocalInstanceDescriptorList)

    const newNodePath = scope.pureLambda(scope.tuple(nodePath, instance), ([localNodePath, instance]) => [...localNodePath, instance], "newPath", {
        output: mattressNodePathArrayDesc,
    })

    const newObjectPath = scope.pureLambda(scope.tuple(objectPath, instance), ([objectPath, instance]) => [...objectPath, instance], "newObjectPath")

    const {
        localNodesDataList,
        curvesDataList,
        referenceFrameDataList,
        cutsDataList,
        componentDescriptorList,
        templateDescriptorList,
        componentOutputs,
        templateOutputs,
        infoDataList,
    } = scope.node(CompileRunMattressComponent, {
        templateContext,
        graphData: graphDataValid,
        templateInputs: templateInputsData,
        componentInputs: componentInputsData,
        partialMattressMeshData: mattressMeshData,
        nodePath: newNodePath,
        objectPath: newObjectPath,
        componentDepth,
        exposeClaimedSubComponentInputs: false,
        forwardedLocalInstanceTemplateOutputs: thisForwardedLocalInstanceTemplateOutputsData,
        forwardedLocalInstanceDescriptorList: thisForwardedLocalInstanceDescriptorListData,
    })

    const [localNodesDataListValid] = scope.branch(localNodesDataList)
    localNodesDataLists.push(localNodesDataListValid)

    const [curvesDataListValid] = scope.branch(curvesDataList)
    curvesDataLists.push(curvesDataListValid)

    const [referenceFramesDataListValid] = scope.branch(referenceFrameDataList)
    referenceFramesDataLists.push(referenceFramesDataListValid)

    const [cutsDataListValid] = scope.branch(cutsDataList)
    cutsDataLists.push(cutsDataListValid)

    const [componentDescriptorListValid] = scope.branch(componentDescriptorList)
    const wrappedComponentDescriptorList = scope.pureLambda(
        scope.tuple(componentDescriptorListValid, id, claimedComponentInputsIds, exposeClaimedSubComponentInputs),
        ([descriptors, componentInstanceId, claimedComponentInputsIds, exposeClaimedSubComponentInputs]) =>
            filterAndWrapInterface(descriptors, componentInstanceId, claimedComponentInputsIds, exposeClaimedSubComponentInputs),
        "wrapComponentDescriptors",
        {
            output: mattressComponentInterfaceDescriptorArrayDesc,
        },
    )
    componentsDescriptorLists.push(wrappedComponentDescriptorList)

    const outputChainingInstanceId = internalParameters?.templateInstance.parameters.id ?? id
    const [templateDescriptorListValid] = scope.branch(templateDescriptorList)
    if (inputSourceMapping.size > 0) {
        inputSourceMappings.push({mapping: inputSourceMapping, rawDescriptorList: templateDescriptorListValid, nodeId: outputChainingInstanceId})
    }
    const wrappedTemplateDescriptorList = scope.pureLambda(
        scope.tuple(
            templateDescriptorListValid,
            id,
            claimedTemplateInputsIds,
            claimedComponentInputsIds,
            exposeClaimedSubComponentInputs,
            outputChainingInstanceId,
        ),
        ([descriptors, componentInstanceId, claimedTemplateInputsIds, claimedComponentInputsIds, exposeClaimedSubComponentInputs, outputChainingInstanceId]) =>
            filterAndWrapInterface(
                descriptors,
                componentInstanceId,
                [...claimedTemplateInputsIds, ...claimedComponentInputsIds],
                exposeClaimedSubComponentInputs,
                outputChainingInstanceId,
            ),
        "wrapTemplateDescriptors",
        {
            output: mattressTemplateInterfaceDescriptorArrayDesc,
        },
    )
    templatesDescriptorLists.push(wrappedTemplateDescriptorList)

    const localId = evaluator.getLocalId(instance)
    templateScope.alias(componentOutputs, `componentOutputs-${localId}`)
    if (internalParameters) {
        templateScope.alias(templateOutputs, `templateOutputs-${evaluator.getLocalId(internalParameters.templateInstance)}`)
    }

    subComponentsOutputs.push(scope.phi(wrapComponentOutputsIds(scope, componentOutputs, id, "components"), EMPTY_OBJECT))
    subTemplatesOutputs.push(scope.phi(wrapComponentOutputsIds(scope, templateOutputs, id, "templates"), EMPTY_OBJECT))

    const [infoDataListValid] = scope.branch(infoDataList)
    infoDataLists.push(infoDataListValid)
}

function createLocalExportForLocalSubInstanceOutput(output: OutputNode, id: string): ExportNode<unknown> {
    if (output instanceof NumberOutput)
        return new NumberExport({
            parameters: {
                name: LOCAL_EXPORT_FOR_LOCAL_SUB_INSTANCE_OUTPUT_NAME_PREFIX,
                node: output,
                id,
            },
        })
    else if (output instanceof MaterialOutput)
        return new MaterialExport({
            parameters: {
                name: LOCAL_EXPORT_FOR_LOCAL_SUB_INSTANCE_OUTPUT_NAME_PREFIX,
                node: output,
                id,
            },
        })
    throw new Error(`Unsupported output type for local sub instance output: ${output}`)
}

//////////////////////////////////////////////////////////////////////////////////////////////

function onCompileImplForTemplateGraph(
    instance: MattressComponentInstance,
    scope: GraphBuilderScope,
    evaluator: NodeEvaluator,
    mattressContext: MattressOnCompileContext["mattressContext"],
    parameters: MattressComponentInstanceParameters,
    graph: TemplateLikeNode,
) {
    const {templateScope} = evaluator
    const {
        evaluatedData,
        nodePath,
        componentInputs: ambientComponentInputs,
        evaluatePartialMattressMeshData,
        forwardedLocalInstanceTemplateOutputs,
        forwardedLocalInstanceDescriptorList,
        exposeClaimedSubComponentInputs,
    } = mattressContext
    const {localNodesDataLists, templatesDescriptorLists, inputSourceMappings} = evaluatedData

    const {id, parameters: instanceParameters, internal: internalParameters} = parameters

    const mattressMeshData = evaluatePartialMattressMeshData(scope, evaluator)

    const [graphDataValid] = scope.branch(evaluator.evaluate(graph))

    const {templateInputsData, claimedTemplateInputsIds, componentInputsData, claimedComponentInputsIds} = evaluateComponentInstanceInputsData(
        scope,
        evaluator,
        id,
        instanceParameters,
        ambientComponentInputs,
        mattressMeshData,
    )

    const inputSourceMapping = buildInputSourceMappingFromInstanceParameters(instanceParameters)

    const newNodePath = scope.pureLambda(scope.tuple(nodePath, instance), ([localNodePath, instance]) => [...localNodePath, instance], "newPath", {
        output: mattressNodePathArrayDesc,
    })

    const localTemplateInputId = scope.const(() => uuid4(), "localTemplateInputId")

    const localTemplateInput = scope.pureLambda(
        localTemplateInputId,
        (localTemplateInputId) => new TemplateGraphInput({parameters: {id: localTemplateInputId, name: ""}}),
        "localTemplateInput",
    )

    const localSubInstanceId = scope.pureLambda(newNodePath, (path) => getLocalSubTemplateInstanceIdFromNodePath(path), "localSubInstanceId")

    const localSubInstanceSrc = scope.pureLambda(
        scope.tuple(localTemplateInput, localSubInstanceId),
        ([localTemplateInput, localSubInstanceId]) =>
            new TemplateInstance({
                parameters: {
                    name: "",
                    id: localSubInstanceId,
                    template: localTemplateInput,
                    parameters: new Parameters({parameters: {}}),
                    visible: true,
                    lockedTransform: Matrix4.identity().toArray(),
                },
            }),
        "localSubInstanceSrc",
    )

    const localSubInstanceData = scope.pureLambda(
        scope.tuple(templateInputsData, componentInputsData, localSubInstanceId),
        ([templateInputs, componentInputs, localSubInstanceId]) => {
            const localSubInstanceParameters: ParametersParameters = {}
            const localSubInstanceLocalInputsData: EvaluatedTemplateInputs = {}

            {
                const templateInputsIds = Object.keys(templateInputs)
                for (const templateInputId of templateInputsIds) {
                    const inputData = templateInputs[templateInputId]
                    if (inputData === undefined || inputData === NotReady) continue

                    const wrappedLocalInputId = wrapInterfaceId(localSubInstanceId, templateInputId)
                    localSubInstanceLocalInputsData[wrappedLocalInputId] = inputData
                }
            }

            {
                const componentInputKeys = Object.keys(componentInputs)
                for (const componentInputKey of componentInputKeys) {
                    const componentInputData = componentInputs[componentInputKey]
                    if (componentInputData === undefined || componentInputData === NotReady || componentInputData.value === null) continue

                    const componentInputDataValue = componentInputData.value
                    const componentInputDataOrigin = componentInputData.origin
                    const componentInputDataType = componentInputData.type

                    if (componentInputDataType === "mattressCurve") {
                        localSubInstanceParameters[componentInputKey] = (componentInputDataValue as MattressCurveData).localNode
                    } else if (componentInputDataType === "mattressBaseMesh") {
                        localSubInstanceParameters[componentInputKey] = (componentInputDataValue as MattressBaseMeshData).localNode
                    } else throw new Error(`Unsupported component input type for instance parameter ${componentInputKey}: ${componentInputDataOrigin}`)
                }
            }

            return {localSubInstanceParameters, localSubInstanceLocalInputsData}
        },
        "localSubInstanceData",
    )

    const localSubInstanceParameters = scope.get(localSubInstanceData, "localSubInstanceParameters")
    const localSubInstanceLocalInputsData = scope.get(localSubInstanceData, "localSubInstanceLocalInputsData")

    const localSubInstance = scope.pureLambda(
        scope.tuple(localSubInstanceSrc, localSubInstanceParameters),
        ([localSubInstanceSrc, localSubInstanceParameters]) => {
            return localSubInstanceSrc.clone({
                cloneInstanceIds: true,
                cloneSubNode: () => false,
                parameterOverrides: {
                    parameters: new Parameters({parameters: {...localSubInstanceParameters}}),
                },
            })
        },
        "localInstance",
    )

    const outputsReferencingComponentInstance = scope.list(
        internalParameters
            ? (Array.from(internalParameters.templateInstance.parents).filter((node) => node instanceof OutputNode) as OutputNode[])
            : (EMPTY_LIST as OutputNode[]),
    )

    const localSubInstanceOutputsLocalNodes = scope.pureLambda(
        scope.tuple(localSubInstance, outputsReferencingComponentInstance, newNodePath),
        ([localSubInstance, outputsReferencingComponentInstance, newNodePath]) => {
            const localNodes: (OutputNode | ExportNode<unknown>)[] = []
            const wrappedComponentInstanceId = getWrappedComponentInstanceId(newNodePath)

            for (const outputReferencingComponentInstance of outputsReferencingComponentInstance) {
                const localOutput = outputReferencingComponentInstance.clone({
                    parameterOverrides: {
                        template: localSubInstance,
                    },
                })
                const localExportId = wrapInterfaceId(wrappedComponentInstanceId, outputReferencingComponentInstance.parameters.outputId)
                const localExport = createLocalExportForLocalSubInstanceOutput(localOutput, localExportId)
                localNodes.push(localOutput, localExport)
            }

            return localNodes
        },
        "localSubInstanceOutputsLocalNodes",
    )

    if (internalParameters) {
        const thisForwardedLocalInstanceTemplateOutputsData = getForwardedLocalInstanceTemplateOutputsForInstance(
            scope,
            id,
            forwardedLocalInstanceTemplateOutputs,
        )
        templateScope.alias(thisForwardedLocalInstanceTemplateOutputsData, `templateOutputs-${evaluator.getLocalId(internalParameters.templateInstance)}`)
    }

    const localNodes = scope.pureLambda(
        scope.tuple(localSubInstance, localTemplateInput, localSubInstanceOutputsLocalNodes),
        ([localSubInstance, localTemplateInput, localSubInstanceOutputsLocalNodes]) => [
            localSubInstance,
            localTemplateInput,
            ...localSubInstanceOutputsLocalNodes,
        ],
        "localNodes",
    )

    const localInputsData = scope.pureLambda(
        scope.tuple(localTemplateInputId, graphDataValid, localSubInstanceLocalInputsData),
        ([localTemplateInputId, graphData, localSubInstanceLocalInputsData]) => {
            const localInputsData: EvaluatedTemplateInputs = {
                [localTemplateInputId]: {value: graphData, type: "TemplateGraph", ancestry: ["TemplateGraph"]},
                ...localSubInstanceLocalInputsData,
            }
            return localInputsData
        },
        "localInputsData",
    )

    const localNodesData = scope.struct<MattressComponentInstanceLocalNodesData>("localNodesData", {
        type: "mattressComponentInstance",
        nodes: localNodes,
        path: newNodePath,
        inputsData: localInputsData,
        localSubInstance: localSubInstance,
    })
    const localNodesDataList = scope.phi(
        scope.pureLambda(localNodesData, (localNodesData) => (localNodesData ? [localNodesData] : EMPTY_LIST), "localNodesDataList"),
        EMPTY_LIST,
    )
    localNodesDataLists.push(localNodesDataList)

    const thisForwardedLocalInstanceDescriptorList = getForwardedLocalInstanceDescriptorListForInstance(scope, id, forwardedLocalInstanceDescriptorList)
    if (inputSourceMapping.size > 0) {
        inputSourceMappings.push({mapping: inputSourceMapping, rawDescriptorList: thisForwardedLocalInstanceDescriptorList, nodeId: id})
    }
    const wrappedTemplateDescriptorList = scope.pureLambda(
        scope.tuple(thisForwardedLocalInstanceDescriptorList, id, claimedTemplateInputsIds, claimedComponentInputsIds, exposeClaimedSubComponentInputs),
        ([descriptors, componentInstanceId, claimedTemplateInputsIds, claimedComponentInputsIds, exposeClaimedSubComponentInputs]) =>
            filterAndWrapInterface(
                descriptors,
                componentInstanceId,
                [...claimedTemplateInputsIds, ...claimedComponentInputsIds],
                exposeClaimedSubComponentInputs,
            ),
        "wrapTemplateDescriptors",
    )
    templatesDescriptorLists.push(wrappedTemplateDescriptorList)
}

@registerTemplateGraphNode
export class MattressComponentInstance extends MattressNode<MattressComponentInstanceParameters> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressComponentInstanceParameters> {
        if (!this.parameters.graph) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressComponentInstanceParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {graph} = this.parameters
        if (graph === null) return skipped

        const scope = evaluator.getScope(this)

        if (isMattressComponentGraphLikeNode(graph)) onCompileImplForMattressComponentGraph(this, scope, evaluator, mattressContext, this.parameters, graph)
        else onCompileImplForTemplateGraph(this, scope, evaluator, mattressContext, this.parameters, graph)

        return visitNone(this.parameters)
    }

    createOutput(
        outputId: string,
        params:
            | {
                  source: "component"
                  outputNodeClass: typeof MattressCurveOutput | typeof MattressReferenceFrameOutput
                  name: string
              }
            | {
                  source: "template"
                  outputNodeClass: typeof NumberOutput | typeof MaterialOutput
                  name: string
              },
    ): MattressCurveOutput | MattressReferenceFrameOutput | NumberOutput | MaterialOutput {
        const source = params.source

        if (source === "component") {
            const {outputNodeClass, name} = params
            return new outputNodeClass({
                parameters: {
                    name: `${name} (${this.parameters.name})`,
                    outputId,
                    component: this,
                },
            })
        } else if (source === "template") {
            const {outputNodeClass, name} = params
            const dummyTemplateInstance = (() => {
                if (this.parameters.internal?.templateInstance) return this.parameters.internal.templateInstance
                const templateInstance = createDummyTemplateInstance()
                this.updateParameters({internal: {templateInstance}})
                return templateInstance
            })()

            return new outputNodeClass({
                parameters: {
                    name: `${name} (${this.parameters.name})`,
                    outputId,
                    template: dummyTemplateInstance,
                },
            })
        } else throw new Error(`Unsupported mattress component output type: ${source}`)
    }

    createOutputForExport(
        exportNode: MattressExport | NumberExport | MaterialExport,
    ): MattressCurveOutput | MattressReferenceFrameOutput | NumberOutput | MaterialOutput {
        const outputInfo = () => {
            if (exportNode instanceof MattressCurveExport)
                return {source: "component" as const, outputNodeClass: MattressCurveOutput, name: exportNode.parameters.name}
            else if (exportNode instanceof MattressReferenceFrameExport)
                return {source: "component" as const, outputNodeClass: MattressReferenceFrameOutput, name: exportNode.parameters.name}
            else if (exportNode instanceof NumberExport) return {source: "template" as const, outputNodeClass: NumberOutput, name: exportNode.parameters.name}
            else if (exportNode instanceof MaterialExport)
                return {source: "template" as const, outputNodeClass: MaterialOutput, name: exportNode.parameters.name}
            throw new Error(`Unsupported export node type for mattress component output: ${exportNode}`)
        }

        return this.createOutput(exportNode.parameters.id, outputInfo())
    }

    addInstanceOutputToGraph(
        outputDescriptor: InterfaceDescriptor<unknown> | MattressComponentInterfaceDescriptor<unknown>,
        graph: MattressComponentGraph,
        {modifyTemplateGraph}: IModifyTemplateGraph,
    ): void {
        modifyTemplateGraph(() => {
            const [_, unwrappedInterfaceId] = getInterfaceIdPrefix(outputDescriptor.props.id)
            const outputInfo = outputAndExportInfoForOutputInterfaceDescriptor(outputDescriptor)
            const outputNode = this.createOutput(unwrappedInterfaceId, outputInfo)
            graph.parameters.nodes.addEntry(outputNode)
        })
    }
}

export const mattressComponentInstance = z.custom<MattressComponentInstance>((x) => x instanceof MattressComponentInstance)

/////////////////////////////////////////////////////////////////
// Component-instance id helpers.

// Builds a slash-separated id like `outerInstanceId/innerInstanceId` by walking the node path from
// outermost MattressComponentInstance inward (optionally cutting off at `instance` if provided).
export function getWrappedComponentInstanceId(path: MattressNodePath, instance?: MattressComponentInstance): string {
    const effectivePath = instance ? path.slice(0, path.findIndex((node) => node === instance) + 1) : path
    return [...effectivePath].reverse().reduce((id, node) => {
        if (node instanceof MattressComponentInstance) return id === "" ? node.parameters.id : wrapInterfaceId(node.parameters.id, id)
        else return id
    }, "")
}

export function getLocalSubTemplateInstanceIdFromNodePath(path: MattressNodePath): string {
    return `local-sub-template-instance-${hashObject({path: getWrappedComponentInstanceId(path)})}`
}
